import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import {
  FaMoneyBillWave,
  FaCreditCard,
  FaUniversity,
  FaExclamationTriangle,
  FaImage,
} from 'react-icons/fa';
import formatAplicaciones from '../../utils/formatAplicaciones';

const POLL_MS = 45000;
const HIGHLIGHT_MS = 7500;
const OVERLAY_MS = 10000;
const SCROLL_OBSERVE_MS = 1200;
const STICKY_BAR_PAD = 118;
const MONITOR_PIN_STORAGE_KEY = 'oso_monitor_pin';
const APPS_MAX_CHARS_DENSE = 72;
const APPS_MAX_CHARS_OVERLAY = 110;

function formatAppsMonitor(raw, maxChars) {
  const formatted = formatAplicaciones(raw);
  if (!formatted || formatted === 'General') return formatted;
  if (formatted.length <= maxChars) return formatted;
  return `${formatted.slice(0, Math.max(0, maxChars - 1))}…`;
}

const CANAL_LABEL = {
  MELI: 'Mercado Libre',
  TIENDA_VIRTUAL: 'Tienda virtual',
  MECANICOS: 'Taller / Mecánico',
  MOSTRADOR: 'Mostrador',
};

function bogotaTodayYmd() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function formatCurrency(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
}

function formatTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleTimeString('es-CO', {
      timeZone: 'America/Bogota',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(value);
  }
}

function formatClock(date) {
  return date.toLocaleTimeString('es-CO', {
    timeZone: 'America/Bogota',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatMedio(medio) {
  const valor = formatCurrency(medio.valor);
  const banco = medio.nombre_banco ? ` · ${medio.nombre_banco}` : '';
  return `${medio.nombre_medio_pago || 'Medio'}${banco}: ${valor}`;
}

function formatMargen(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return '—';
  return `${Number(pct).toFixed(1)}%`;
}

function isPerdidaMargen(pct) {
  const n = Number(pct);
  return Number.isFinite(n) && n < 0;
}

function margenBadgeStyle(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n)) {
    return { background: '#546e7a', color: '#eceff1' };
  }
  if (n < 0) return { background: '#d50000', color: '#ffffff' };
  if (n > 30) return { background: '#c8e6c9', color: '#1b5e20' };
  if (n > 15) return { background: '#fff9c4', color: '#f57f17' };
  return { background: '#ffcdd2', color: '#b71c1c' };
}

function isPreElectronico(venta) {
  const tipo = Number(venta.tipoDocumento);
  if (tipo === 9) return true;
  if (tipo === 1) return false;
  return !venta.nFactura;
}

function documentTitle(venta) {
  if (isPreElectronico(venta)) {
    return `Pre-electrónico # ${venta.n_transacion || venta.id_transacion}`;
  }
  return venta.nFactura ? `Factura # ${venta.nFactura}` : `Factura # ${venta.id_transacion}`;
}

function MedioIcon({ idMedioPago, size = 16, style }) {
  const id = Number(idMedioPago);
  const common = { size, style: { flexShrink: 0, ...style } };
  if (id === 1) return <FaMoneyBillWave {...common} title="Efectivo" />;
  if (id === 2 || id === 3) return <FaCreditCard {...common} title="Tarjeta" />;
  if (id === 7) return <FaUniversity {...common} title="Transferencia" />;
  return <FaExclamationTriangle {...common} title="Otro / crédito" />;
}

function formatBancosInline(bancosList) {
  if (!bancosList?.length) return '';
  return bancosList
    .map((b) => `${b.nombre} ${formatCurrency(b.valor)}`)
    .join(' · ');
}

function mergeVentas(prev, incoming) {
  if (!incoming?.id_transacion) return prev;
  const idx = prev.findIndex((v) => Number(v.id_transacion) === Number(incoming.id_transacion));
  if (idx === -1) return [...prev, incoming];
  const next = [...prev];
  next[idx] = { ...next[idx], ...incoming };
  return next;
}

function summarizeMedios(ventas) {
  const activas = (ventas || []).filter((v) => !v.es_anulada);
  let efectivo = 0;
  let tarjetas = 0;
  let transferencia = 0;
  let credito = 0;
  const bancos = {};

  for (const v of activas) {
    if (v.es_credito || !(v.medios || []).length) {
      credito += Number(v.total_neto) || 0;
      continue;
    }
    for (const m of v.medios) {
      const id = Number(m.id_medio_pago);
      const valor = Number(m.valor) || 0;
      if (id === 1) efectivo += valor;
      else if (id === 2 || id === 3) tarjetas += valor;
      else if (id === 7) {
        transferencia += valor;
        const banco = m.nombre_banco || 'Sin banco';
        bancos[banco] = (bancos[banco] || 0) + valor;
      }
    }
  }

  const bancosList = Object.entries(bancos)
    .map(([nombre, valor]) => ({ nombre, valor }))
    .sort((a, b) => b.valor - a.valor);

  return { efectivo, tarjetas, transferencia, credito, bancosList };
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(165deg, #0f1c2e 0%, #1a2f45 45%, #243b53 100%)',
    color: '#f5f7fa',
    fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    padding: '0.65rem 1.5rem',
    background: 'rgba(15, 28, 46, 0.94)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    backdropFilter: 'blur(8px)',
    minHeight: 52,
  },
  title: {
    margin: 0,
    fontSize: '1.55rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap',
  },
  titleDate: {
    fontWeight: 500,
    opacity: 0.75,
    fontSize: '1.15rem',
  },
  meta: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '1rem',
    alignItems: 'center',
    fontSize: '1.05rem',
    whiteSpace: 'nowrap',
  },
  live: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    fontWeight: 700,
    color: '#7dffa3',
  },
  liveDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#3ddc84',
    boxShadow: '0 0 0 3px rgba(61, 220, 132, 0.25)',
  },
  liveOff: {
    color: '#ffb4a8',
  },
  feed: {
    flex: 1,
    padding: '1rem 1.5rem 7.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem',
    maxWidth: 1400,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    scrollPaddingBottom: STICKY_BAR_PAD,
  },
  empty: {
    marginTop: '3rem',
    textAlign: 'center',
    fontSize: '1.35rem',
    opacity: 0.7,
  },
  error: {
    padding: '0.85rem 1rem',
    background: 'rgba(180, 40, 40, 0.35)',
    border: '1px solid rgba(255,120,120,0.5)',
    borderRadius: 8,
    fontSize: '1.05rem',
  },
  alertBadge: {
    padding: '0.28rem 0.6rem',
    borderRadius: 6,
    fontWeight: 900,
    fontSize: '0.9rem',
    color: '#fff',
  },
  stickyBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 30,
    minHeight: 60,
    padding: '0.45rem 1.25rem 0.55rem',
    background: 'rgba(8, 16, 28, 0.97)',
    borderTop: '2px solid rgba(125, 255, 163, 0.35)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '0.3rem',
    boxSizing: 'border-box',
  },
  stickyMediosRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    width: '100%',
  },
  stickyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.55rem',
    minWidth: 0,
    flex: '1 1 0',
  },
  stickyIcon: {
    fontSize: '1.2rem',
    flexShrink: 0,
  },
  stickyLabel: {
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    opacity: 0.7,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  stickyAmount: {
    fontSize: '1.35rem',
    fontWeight: 900,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
  },
  stickyBancosRow: {
    width: '100%',
    textAlign: 'center',
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#ffcc80',
    lineHeight: 1.35,
    padding: '0 0.5rem',
    boxSizing: 'border-box',
  },
};

function canalBadgeStyle(canal) {
  const map = {
    MELI: { bg: '#ffe600', color: '#1a1a1a' },
    TIENDA_VIRTUAL: { bg: '#4fc3f7', color: '#062433' },
    MECANICOS: { bg: '#ffb74d', color: '#3b2200' },
    MOSTRADOR: { bg: '#90a4ae', color: '#102027' },
  };
  const c = map[canal] || map.MOSTRADOR;
  return {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: 6,
    fontWeight: 800,
    fontSize: '0.85rem',
    background: c.bg,
    color: c.color,
  };
}

function MargenPill({ pct, label }) {
  const perdida = isPerdidaMargen(pct);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
      {label ? (
        <span style={{ fontSize: '0.85rem', opacity: perdida ? 1 : 0.85, fontWeight: perdida ? 800 : 500, color: perdida ? '#ff8a80' : undefined }}>
          {perdida ? 'Pérdida' : label}
        </span>
      ) : null}
      <span
        style={{
          display: 'inline-block',
          padding: perdida ? '0.2rem 0.55rem' : '0.15rem 0.45rem',
          borderRadius: 6,
          fontWeight: 900,
          fontSize: perdida ? '0.95rem' : '0.85rem',
          letterSpacing: perdida ? '0.02em' : undefined,
          boxShadow: perdida ? '0 0 0 2px rgba(213, 0, 0, 0.55)' : undefined,
          ...margenBadgeStyle(pct),
        }}
      >
        {formatMargen(pct)}
      </span>
    </span>
  );
}

function ShimmerBlock({ style }) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.12)',
        borderRadius: 8,
        animation: 'monitorPulse 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

function MonitorSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            padding: '1.15rem 1.25rem',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <ShimmerBlock style={{ width: 100, height: 18, marginBottom: 8 }} />
              <ShimmerBlock style={{ width: '50%', height: 22, marginBottom: 6 }} />
              <ShimmerBlock style={{ width: '28%', height: 14 }} />
            </div>
            <div style={{ width: 140 }}>
              <ShimmerBlock style={{ width: '100%', height: 28, marginBottom: 6 }} />
              <ShimmerBlock style={{ width: '65%', height: 14, marginLeft: 'auto' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '0.85rem' }}>
            <ShimmerBlock style={{ width: '100%', height: 48 }} />
            <ShimmerBlock style={{ width: '100%', height: 48 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function StickyCajaItem({ icon, label, amount, amountColor, iconColor }) {
  return (
    <div style={styles.stickyItem}>
      <span style={{ ...styles.stickyIcon, color: iconColor || '#7dffa3' }}>{icon}</span>
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <div style={styles.stickyLabel}>{label}</div>
        <div style={{ ...styles.stickyAmount, color: amountColor }}>{formatCurrency(amount)}</div>
      </div>
    </div>
  );
}

function MonitorItemRow({ item, isMeli, dense = false, eager = false }) {
  const thumb = dense ? 56 : 64;
  const appsFull = item.aplicaciones ? formatAplicaciones(item.aplicaciones) : null;
  const appsShown = item.aplicaciones
    ? formatAppsMonitor(item.aplicaciones, dense ? APPS_MAX_CHARS_DENSE : APPS_MAX_CHARS_OVERLAY)
    : null;
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: dense ? '0.65rem' : '0.85rem',
        padding: dense ? '0.45rem 0' : '0.55rem 0',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        fontSize: dense ? '1.02rem' : '1.15rem',
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: thumb,
          height: thumb,
          flexShrink: 0,
          borderRadius: 8,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {item.imagen_url ? (
          <img
            src={item.imagen_url}
            alt=""
            width={thumb}
            height={thumb}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }}
          />
        ) : (
          <FaImage style={{ opacity: 0.35, fontSize: dense ? '1.1rem' : '1.35rem' }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>
          <strong>{item.cantidad}</strong>
          {' × '}
          {item.descripcion}
        </div>
        {(item.sku || item.codigo_barras) && (
          <div style={{ marginTop: 2, fontSize: dense ? '0.82rem' : '0.92rem', opacity: 0.8 }}>
            {item.sku && (
              <>
                <span style={{ fontWeight: 700 }}>SKU:</span>
                {' '}
                {item.sku}
              </>
            )}
            {item.codigo_barras && (
              <>
                {item.sku ? <span style={{ opacity: 0.4 }}> · </span> : null}
                <span style={{ fontWeight: 700 }}>Cód. barras:</span>
                {' '}
                {item.codigo_barras}
              </>
            )}
          </div>
        )}
        {appsShown && (
          <div
            title={appsFull}
            style={{
              marginTop: 2,
              fontSize: dense ? '0.8rem' : '0.88rem',
              opacity: 0.72,
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontWeight: 700 }}>Apps:</span>
            {' '}
            {appsShown}
          </div>
        )}
        <div style={{ marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center', fontSize: dense ? '0.85rem' : '0.95rem' }}>
          {item.es_infinito && (
            <span style={{
              padding: '0.12rem 0.4rem',
              borderRadius: 4,
              background: 'rgba(129, 140, 248, 0.35)',
              color: '#c7d2fe',
              fontWeight: 800,
            }}
            >
              Producto infinito
            </span>
          )}
          {item.agotado && (
            <span style={{
              padding: '0.12rem 0.45rem',
              borderRadius: 4,
              background: '#d50000',
              color: '#fff',
              fontWeight: 900,
              letterSpacing: '0.03em',
            }}
            >
              AGOTADO
            </span>
          )}
          {item.existencias != null && !item.es_infinito && (
            <span>
              <span style={{ opacity: 0.7, fontWeight: 600 }}>Existencias:</span>
              {' '}
              <span style={{
                fontWeight: 900,
                background: item.agotado ? 'rgba(213,0,0,0.35)' : 'rgba(0,0,0,0.45)',
                color: item.agotado ? '#ff8a80' : '#ffeb3b',
                padding: '0.1rem 0.45rem',
                borderRadius: 999,
              }}
              >
                {Number(item.existencias).toFixed(1)}
              </span>
            </span>
          )}
          {item.tiene_descuento && (
            <span style={{ color: '#ce93d8', fontWeight: 700 }}>
              Desc. {formatCurrency(item.descuento_con_iva ?? item.descuento_valor)}
            </span>
          )}
          {item.precio_distinto_lista && (
            <span style={{ color: '#81d4fa', fontWeight: 700 }}>
              Lista {formatCurrency(item.precio_lista)} → fact. {formatCurrency(item.precio_venta)}
            </span>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
        <div style={{ fontWeight: 700 }}>{formatCurrency(item.total)}</div>
        {!isMeli && !item.es_infinito && item.margen_pct != null && (
          <div style={{ marginTop: '0.2rem' }}>
            <MargenPill pct={item.margen_pct} />
          </div>
        )}
      </div>
    </li>
  );
}

function VentaBlock({ venta, highlight, blockRef }) {
  const anulada = Boolean(venta.es_anulada);
  const credito = Boolean(venta.es_credito) && !anulada;
  const voucherPendiente = Boolean(venta.tiene_autorizacion_pendiente) && !anulada;
  const perdida = !anulada && isPerdidaMargen(venta.margen_pct);
  const preElectronico = isPreElectronico(venta);
  const isMeli = venta.canal === 'MELI';

  return (
    <article
      ref={blockRef}
      className={highlight ? 'monitor-venta-highlight' : undefined}
      style={{
        padding: '1.1rem 1.25rem',
        borderRadius: 10,
        background: anulada
          ? 'rgba(80, 30, 30, 0.55)'
          : perdida
            ? 'rgba(160, 0, 0, 0.55)'
            : credito || voucherPendiente
              ? 'rgba(120, 35, 35, 0.45)'
              : 'rgba(255,255,255,0.07)',
        border: highlight
          ? '3px solid #7dffa3'
          : perdida
            ? '3px solid #ff1744'
            : credito || voucherPendiente
              ? '2px solid #ff6b6b'
              : anulada
                ? '2px solid #c62828'
                : '1px solid rgba(255,255,255,0.12)',
        boxShadow: perdida && !highlight ? '0 0 20px rgba(255, 23, 68, 0.35)' : undefined,
        transform: highlight ? 'scale(1.01)' : 'scale(1)',
        transition: 'transform 0.35s ease, border-color 0.35s ease',
        opacity: anulada ? 0.85 : 1,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.7rem' }}>
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', alignItems: 'center', marginBottom: '0.35rem' }}>
            <span style={canalBadgeStyle(venta.canal)}>{CANAL_LABEL[venta.canal] || venta.canal}</span>
            {anulada && (
              <span style={{ ...styles.alertBadge, background: '#c62828', letterSpacing: '0.04em' }}>
                ANULADA
              </span>
            )}
            {perdida && (
              <span style={{
                ...styles.alertBadge,
                background: '#d50000',
                fontSize: '1.05rem',
                letterSpacing: '0.06em',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.25)',
              }}
              >
                PÉRDIDA
              </span>
            )}
            {credito && (
              <span style={{ ...styles.alertBadge, background: '#ff1744' }}>
                CRÉDITO — Sin recibo de caja
              </span>
            )}
            {voucherPendiente && (
              <span style={{ ...styles.alertBadge, background: '#ff6f00' }}>
                Pendiente autorización de voucher
              </span>
            )}
            {venta.tiene_descuento && !anulada && (
              <span style={{ ...styles.alertBadge, background: '#6a1b9a' }}>
                Con descuento
              </span>
            )}
            {venta.tiene_precio_distinto_lista && !anulada && (
              <span style={{ ...styles.alertBadge, background: '#0277bd' }}>
                Precio distinto a lista
              </span>
            )}
          </div>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 800,
            textDecoration: anulada ? 'line-through' : 'none',
          }}
          >
            {documentTitle(venta)}
          </h2>
          <div style={{ marginTop: '0.25rem', fontSize: '0.98rem', opacity: 0.85 }}>
            {formatTime(venta.fecha_registro)}
            {preElectronico ? null : (
              <>
                {' · '}
                <span style={{ opacity: 0.75 }}>Electrónica</span>
              </>
            )}
            {' · '}
            Facturó: <strong>{venta.nombre_empleado || 'No Asignado'}</strong>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: '2rem',
            fontWeight: 900,
            lineHeight: 1.1,
            textDecoration: anulada ? 'line-through' : 'none',
            color: anulada ? '#ffcdd2' : '#fff',
          }}
          >
            {formatCurrency(venta.total_neto)}
          </div>
          {venta.margen_pct != null && (
            <div style={{ marginTop: '0.3rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.4rem' }}>
              <MargenPill
                pct={venta.margen_pct}
                label={venta.margen_aprox || isMeli ? 'Margen aprox.' : 'Margen'}
              />
            </div>
          )}
          {venta.tiene_descuento && Number(venta.descuento_total) > 0 && (
            <div style={{ marginTop: '0.25rem', fontSize: '0.95rem', color: '#ce93d8', fontWeight: 700 }}>
              Descuento:
              {' '}
              {formatCurrency(venta.descuento_total)}
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: '1.1rem', marginBottom: '0.65rem' }}>
        Cliente: <strong>{venta.nombre_cliente || '—'}</strong>
      </div>

      {venta.es_mecanico || venta.canal === 'MECANICOS' ? (
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.65rem 0.85rem',
          borderRadius: 8,
          background: 'rgba(255, 183, 77, 0.18)',
          fontSize: '1.05rem',
        }}
        >
          <div>
            Mecánico: <strong>{venta.nombre_mecanico || '—'}</strong>
            {' · '}
            Placa: <strong>{venta.vehiculo || '—'}</strong>
          </div>
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65, marginBottom: '0.35rem' }}>
            Ítems facturados
          </div>
          {(venta.items || []).length === 0 ? (
            <div style={{ opacity: 0.6 }}>Sin detalle</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {venta.items.map((item) => (
                <MonitorItemRow
                  key={item.id_detalle_transacion || `${item.id_producto}-${item.descripcion}`}
                  item={item}
                  isMeli={isMeli}
                  dense
                />
              ))}
            </ul>
          )}
        </div>

        <div>
          <div style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65, marginBottom: '0.35rem' }}>
            Medio(s) de pago
          </div>
          {credito ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '1.1rem', fontWeight: 800, color: '#ff8a80' }}>
              <FaExclamationTriangle size={16} />
              Venta a crédito (sin recibo de caja)
            </div>
          ) : (venta.medios || []).length === 0 ? (
            <div style={{ opacity: 0.6 }}>—</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {venta.medios.map((medio) => (
                <li
                  key={medio.id_comprobante_caja || `${medio.id_medio_pago}-${medio.valor}`}
                  style={{
                    padding: '0.4rem 0',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    fontSize: '1.02rem',
                    fontWeight: 600,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                    <MedioIcon
                      idMedioPago={medio.id_medio_pago}
                      size={15}
                      style={{
                        color: Number(medio.id_medio_pago) === 1
                          ? '#81c784'
                          : (Number(medio.id_medio_pago) === 2 || Number(medio.id_medio_pago) === 3)
                            ? '#64b5f6'
                            : Number(medio.id_medio_pago) === 7
                              ? '#ffb74d'
                              : '#ce93d8',
                      }}
                    />
                    <span>{formatMedio(medio)}</span>
                  </div>
                  {medio.autorizacion_pendiente && (
                    <div style={{ marginTop: '0.2rem', marginLeft: '1.35rem', color: '#ffb74d', fontWeight: 800, fontSize: '0.92rem' }}>
                      Falta autorización del voucher
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </article>
  );
}

function VentaOverlay({ venta, queueLen }) {
  if (!venta) return null;
  const anulada = Boolean(venta.es_anulada);
  const credito = Boolean(venta.es_credito) && !anulada;
  const perdida = !anulada && isPerdidaMargen(venta.margen_pct);
  const isMeli = venta.canal === 'MELI';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(5, 10, 18, 0.82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        animation: 'monitorOverlayIn 0.35s ease-out',
      }}
    >
      <div
        style={{
          width: 'min(1100px, 96vw)',
          maxHeight: '92vh',
          overflow: 'auto',
          borderRadius: 16,
          background: 'linear-gradient(165deg, #152338 0%, #1e3348 100%)',
          border: perdida ? '3px solid #ff1744' : '3px solid #7dffa3',
          boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
          padding: '1.5rem 1.75rem 1.25rem',
          position: 'relative',
          animation: 'monitorOverlayCard 0.4s ease-out',
        }}
      >
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          height: 5,
          background: 'rgba(255,255,255,0.12)',
          overflow: 'hidden',
          borderRadius: '16px 16px 0 0',
        }}
        >
          <div className="monitor-overlay-progress" />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.45rem', alignItems: 'center' }}>
              <span style={canalBadgeStyle(venta.canal)}>{CANAL_LABEL[venta.canal] || venta.canal}</span>
              {perdida && (
                <span style={{ ...styles.alertBadge, background: '#d50000', fontSize: '1.1rem' }}>PÉRDIDA</span>
              )}
              {credito && (
                <span style={{ ...styles.alertBadge, background: '#ff1744' }}>CRÉDITO</span>
              )}
              {queueLen > 0 && (
                <span style={{ ...styles.alertBadge, background: '#455a64' }}>
                  En cola:
                  {' '}
                  {queueLen}
                </span>
              )}
            </div>
            <h2 style={{ margin: 0, fontSize: '2rem', fontWeight: 900 }}>
              {documentTitle(venta)}
            </h2>
            <div style={{ marginTop: '0.35rem', fontSize: '1.15rem', opacity: 0.85 }}>
              {formatTime(venta.fecha_registro)}
              {' · '}
              Facturó:
              {' '}
              <strong>{venta.nombre_empleado || 'No Asignado'}</strong>
            </div>
            <div style={{ marginTop: '0.45rem', fontSize: '1.25rem' }}>
              Cliente:
              {' '}
              <strong>{venta.nombre_cliente || '—'}</strong>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '2.75rem', fontWeight: 900, lineHeight: 1 }}>
              {formatCurrency(venta.total_neto)}
            </div>
            {venta.margen_pct != null && (
              <div style={{ marginTop: '0.4rem', display: 'flex', justifyContent: 'flex-end' }}>
                <MargenPill
                  pct={venta.margen_pct}
                  label={venta.margen_aprox || isMeli ? 'Margen aprox.' : 'Margen'}
                />
              </div>
            )}
          </div>
        </div>

        {(venta.es_mecanico || venta.canal === 'MECANICOS') && (
          <div style={{
            marginBottom: '0.85rem',
            padding: '0.65rem 0.85rem',
            borderRadius: 8,
            background: 'rgba(255, 183, 77, 0.18)',
            fontSize: '1.1rem',
          }}
          >
            Mecánico:
            {' '}
            <strong>{venta.nombre_mecanico || '—'}</strong>
            {' · '}
            Placa:
            {' '}
            <strong>{venta.vehiculo || '—'}</strong>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1.25rem' }}>
          <div>
            <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65, marginBottom: '0.35rem' }}>
              Ítems
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {(venta.items || []).map((item) => (
                <MonitorItemRow
                  key={item.id_detalle_transacion || `${item.id_producto}-${item.descripcion}`}
                  item={item}
                  isMeli={isMeli}
                  eager
                />
              ))}
            </ul>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65, marginBottom: '0.35rem' }}>
              Medios de pago
            </div>
            {credito ? (
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#ff8a80' }}>
                Venta a crédito (sin recibo de caja)
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {(venta.medios || []).map((medio) => (
                  <li
                    key={medio.id_comprobante_caja || `${medio.id_medio_pago}-${medio.valor}`}
                    style={{ padding: '0.45rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: '1.15rem', fontWeight: 700 }}
                  >
                    {formatMedio(medio)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MonitorVentas() {
  const [pinStored, setPinStored] = useState(() => {
    try {
      return localStorage.getItem(MONITOR_PIN_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const unlocked = Boolean(pinStored);

  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [live, setLive] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [highlightId, setHighlightId] = useState(null);
  const [overlayVenta, setOverlayVenta] = useState(null);
  const [overlayQueueLen, setOverlayQueueLen] = useState(0);

  const highlightTimer = useRef(null);
  const socketRef = useRef(null);
  const ventaRefs = useRef(new Map());
  const didInitialScroll = useRef(false);
  const overlayQueueRef = useRef([]);
  const overlayActiveRef = useRef(null);
  const overlayShownRef = useRef(new Set());
  const bootstrapDoneRef = useRef(false);
  const pollCycleRef = useRef(0);
  const maxIdRef = useRef(0);
  const scrollObserveTimer = useRef(null);
  const resizeObserverRef = useRef(null);

  const fecha = useMemo(() => bogotaTodayYmd(), []);
  const totales = useMemo(() => summarizeMedios(ventas), [ventas]);
  const bancosInline = useMemo(() => formatBancosInline(totales.bancosList), [totales.bancosList]);

  const setVentaRef = useCallback((id, el) => {
    const key = Number(id);
    if (el) ventaRefs.current.set(key, el);
    else ventaRefs.current.delete(key);
  }, []);

  const scrollVentaIntoView = useCallback((id) => {
    const el = ventaRefs.current.get(Number(id));
    if (!el) return;

    const doScroll = () => {
      el.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };
    doScroll();

    if (resizeObserverRef.current) {
      try { resizeObserverRef.current.disconnect(); } catch { /* ignore */ }
    }
    if (scrollObserveTimer.current) clearTimeout(scrollObserveTimer.current);

    const ro = new ResizeObserver(() => {
      doScroll();
    });
    resizeObserverRef.current = ro;
    ro.observe(el);
    scrollObserveTimer.current = setTimeout(() => {
      try { ro.disconnect(); } catch { /* ignore */ }
      if (resizeObserverRef.current === ro) resizeObserverRef.current = null;
    }, SCROLL_OBSERVE_MS);
  }, []);

  const focusVentaInList = useCallback((id) => {
    setHighlightId(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollVentaIntoView(id));
    });
  }, [scrollVentaIntoView]);

  const enqueueOverlay = useCallback((venta) => {
    if (!venta?.id_transacion) return;
    const id = Number(venta.id_transacion);
    const alreadyQueued = overlayQueueRef.current.some((v) => Number(v.id_transacion) === id);
    const showing = overlayActiveRef.current != null && Number(overlayActiveRef.current) === id;
    if (alreadyQueued || showing) {
      // refresh payload in queue / current overlay
      overlayQueueRef.current = overlayQueueRef.current.map((v) => (
        Number(v.id_transacion) === id ? { ...v, ...venta } : v
      ));
      if (showing) {
        setOverlayVenta((prev) => (prev && Number(prev.id_transacion) === id ? { ...prev, ...venta } : prev));
      }
      return;
    }

    if (overlayActiveRef.current == null) {
      overlayActiveRef.current = id;
      setOverlayVenta(venta);
      setOverlayQueueLen(overlayQueueRef.current.length);
      return;
    }

    overlayQueueRef.current.push(venta);
    setOverlayQueueLen(overlayQueueRef.current.length);
  }, []);

  const noteMaxId = useCallback((list) => {
    let max = maxIdRef.current;
    for (const v of list || []) {
      const id = Number(v.id_transacion);
      if (Number.isFinite(id) && id > max) max = id;
    }
    maxIdRef.current = max;
  }, []);

  const enqueueNewVentas = useCallback((list, { allowOverlay }) => {
    for (const venta of list || []) {
      const id = Number(venta.id_transacion);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (overlayShownRef.current.has(id)) continue;
      overlayShownRef.current.add(id);
      if (allowOverlay) enqueueOverlay(venta);
    }
  }, [enqueueOverlay]);

  const unlockWithPin = async (e) => {
    e.preventDefault();
    const pin = pinInput.trim();
    if (!pin) {
      setPinError('Ingresa el PIN');
      return;
    }
    setPinBusy(true);
    setPinError('');
    try {
      await axios.post('/api/monitor/auth', { pin });
      localStorage.setItem(MONITOR_PIN_STORAGE_KEY, pin);
      setPinStored(pin);
      setLoading(true);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'MONITOR_PIN_INVALID') {
        setPinError('PIN incorrecto');
      } else if (code === 'MONITOR_PIN_NOT_CONFIGURED') {
        setPinError('El PIN aún no está configurado. Ve a Administración → Config general.');
      } else {
        setPinError(err.response?.data?.message || 'No se pudo validar el PIN.');
      }
    } finally {
      setPinBusy(false);
    }
  };

  const fetchVentas = useCallback(async (mode = 'full') => {
    if (!pinStored) return;
    try {
      setError('');
      const incremental = mode === 'incremental' && maxIdRef.current > 0;
      const params = new URLSearchParams({ fecha });
      if (incremental) params.set('after_id', String(maxIdRef.current));

      const { data } = await axios.get(`/api/monitor/ventas?${params.toString()}`, {
        headers: { 'X-Monitor-Pin': pinStored },
      });
      const incoming = data.data || [];

      if (incremental) {
        if (incoming.length) {
          setVentas((prev) => {
            let next = prev;
            for (const v of incoming) next = mergeVentas(next, v);
            return next;
          });
          noteMaxId(incoming);
          enqueueNewVentas(incoming, { allowOverlay: true });
        }
      } else {
        setVentas(incoming);
        noteMaxId(incoming);
        if (!bootstrapDoneRef.current) {
          enqueueNewVentas(incoming, { allowOverlay: false });
          bootstrapDoneRef.current = true;
        } else {
          enqueueNewVentas(incoming, { allowOverlay: true });
        }
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.code;
      let msg = err.response?.data?.message || 'No se pudieron cargar las ventas.';
      if (code === 'IP_NOT_ALLOWED' || code === 'IP_NOT_CONFIGURED') {
        msg = 'Acceso restringido: PIN inválido o red no autorizada.';
        try {
          localStorage.removeItem(MONITOR_PIN_STORAGE_KEY);
        } catch { /* ignore */ }
        setPinStored('');
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [fecha, pinStored, noteMaxId, enqueueNewVentas]);

  useEffect(() => {
    if (!unlocked) {
      setLoading(false);
      return;
    }
    fetchVentas('full');
  }, [fetchVentas, unlocked]);

  useEffect(() => {
    if (loading || didInitialScroll.current || !ventas.length) return;
    didInitialScroll.current = true;
    const last = ventas[ventas.length - 1];
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollVentaIntoView(last.id_transacion));
    });
  }, [loading, ventas, scrollVentaIntoView]);

  useEffect(() => {
    if (!overlayVenta) return undefined;
    const finishedId = overlayVenta.id_transacion;
    const t = setTimeout(() => {
      const next = overlayQueueRef.current.shift();
      setOverlayQueueLen(overlayQueueRef.current.length);
      if (next) {
        overlayActiveRef.current = Number(next.id_transacion);
        setOverlayVenta(next);
      } else {
        overlayActiveRef.current = null;
        setOverlayVenta(null);
        focusVentaInList(finishedId);
      }
    }, OVERLAY_MS);
    return () => clearTimeout(t);
  }, [overlayVenta, focusVentaInList]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!unlocked) return undefined;
    const poll = setInterval(() => {
      pollCycleRef.current += 1;
      // Cada 4 ciclos (~3 min): full sync (anulaciones / medios). El resto: solo ids nuevos.
      const mode = pollCycleRef.current % 4 === 0 ? 'full' : 'incremental';
      fetchVentas(mode);
    }, POLL_MS);
    return () => clearInterval(poll);
  }, [fetchVentas, unlocked]);

  useEffect(() => {
    if (!unlocked || !pinStored) return undefined;

    const url = `${window.location.protocol}//${window.location.hostname}:${window.location.port || 443}`;
    const monitorSocket = io(url, {
      withCredentials: true,
      autoConnect: false,
      auth: { mode: 'monitor', pin: pinStored },
    });
    socketRef.current = monitorSocket;

    const onVenta = (payload) => {
      if (!payload || payload.pending) {
        fetchVentas('incremental');
        return;
      }
      const id = Number(payload.id_transacion);
      setVentas((prev) => mergeVentas(prev, payload));
      noteMaxId([payload]);
      if (!overlayShownRef.current.has(id)) {
        overlayShownRef.current.add(id);
        enqueueOverlay(payload);
        return;
      }
      const showing = overlayActiveRef.current != null && Number(overlayActiveRef.current) === id;
      const queued = overlayQueueRef.current.some((v) => Number(v.id_transacion) === id);
      if (showing || queued) enqueueOverlay(payload);
    };

    monitorSocket.on('connect', () => setLive(true));
    monitorSocket.on('disconnect', () => setLive(false));
    monitorSocket.on('connect_error', () => setLive(false));
    monitorSocket.on('monitor:venta', onVenta);
    monitorSocket.connect();

    return () => {
      monitorSocket.off('monitor:venta', onVenta);
      monitorSocket.disconnect();
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      if (scrollObserveTimer.current) clearTimeout(scrollObserveTimer.current);
      if (resizeObserverRef.current) {
        try { resizeObserverRef.current.disconnect(); } catch { /* ignore */ }
      }
    };
  }, [fetchVentas, unlocked, pinStored, enqueueOverlay, noteMaxId]);

  const activas = ventas.filter((v) => !v.es_anulada).length;
  const creditos = ventas.filter((v) => v.es_credito && !v.es_anulada).length;
  const vouchersPendientes = ventas.filter((v) => v.tiene_autorizacion_pendiente && !v.es_anulada).length;

  if (!unlocked) {
    return (
      <div style={{
        ...styles.page,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
      >
        <form
          onSubmit={unlockWithPin}
          style={{
            width: '100%',
            maxWidth: 420,
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '2.4rem', fontWeight: 900, margin: '0 0 0.5rem' }}>
            Monitor de ventas
          </h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.75, marginBottom: '1.75rem' }}>
            Ingresa el PIN para continuar. Se guardará en este equipo.
          </p>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            placeholder="PIN"
            style={{
              width: '100%',
              fontSize: '2rem',
              letterSpacing: '0.35em',
              textAlign: 'center',
              padding: '0.85rem 1rem',
              borderRadius: 10,
              border: '2px solid rgba(255,255,255,0.25)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              outline: 'none',
              marginBottom: '1rem',
            }}
          />
          {pinError && (
            <div style={{ color: '#ff8a80', fontWeight: 700, marginBottom: '1rem', fontSize: '1.05rem' }}>
              {pinError}
            </div>
          )}
          <button
            type="submit"
            disabled={pinBusy}
            style={{
              width: '100%',
              fontSize: '1.25rem',
              fontWeight: 800,
              padding: '0.9rem 1.25rem',
              borderRadius: 10,
              border: 'none',
              background: '#ffc107',
              color: '#1a1a1a',
              cursor: pinBusy ? 'wait' : 'pointer',
              opacity: pinBusy ? 0.7 : 1,
            }}
          >
            {pinBusy ? 'Validando…' : 'Entrar'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>
        {`
          @keyframes monitorPulse {
            0%, 100% { opacity: 0.45; }
            50% { opacity: 0.9; }
          }
          @keyframes monitorGlow {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(125, 255, 163, 0.55), 0 0 28px 6px rgba(125, 255, 163, 0.35);
            }
            50% {
              box-shadow: 0 0 0 8px rgba(125, 255, 163, 0.15), 0 0 48px 14px rgba(125, 255, 163, 0.55);
            }
          }
          @keyframes monitorOverlayIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes monitorOverlayCard {
            from { opacity: 0; transform: translateY(18px) scale(0.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes monitorOverlayProgress {
            from { transform: scaleX(1); }
            to { transform: scaleX(0); }
          }
          .monitor-venta-highlight {
            animation: monitorGlow 1.1s ease-in-out infinite;
            z-index: 2;
          }
          .monitor-overlay-progress {
            height: 100%;
            width: 100%;
            transform-origin: left center;
            background: linear-gradient(90deg, #7dffa3, #ffc107);
            animation: monitorOverlayProgress ${OVERLAY_MS}ms linear forwards;
          }
          html {
            scroll-padding-bottom: ${STICKY_BAR_PAD}px;
          }
        `}
      </style>

      {overlayVenta && (
        <VentaOverlay venta={overlayVenta} queueLen={overlayQueueLen} />
      )}

      <header style={styles.header}>
        <h1 style={styles.title}>
          Ventas de hoy
          <span style={styles.titleDate}>
            {' · '}
            {fecha}
          </span>
        </h1>
        <div style={styles.meta}>
          <span style={live ? styles.live : { ...styles.live, ...styles.liveOff }}>
            <span style={{ ...styles.liveDot, background: live ? '#3ddc84' : '#ef5350', boxShadow: live ? styles.liveDot.boxShadow : 'none' }} />
            {live ? 'En vivo' : 'Reconectando…'}
          </span>
          <span>{formatClock(now)}</span>
          <span>
            <strong>{activas}</strong>
            {' '}
            venta
            {activas === 1 ? '' : 's'}
          </span>
          {creditos > 0 && (
            <span style={{ color: '#ff8a80', fontWeight: 800 }}>
              {creditos}
              {' '}
              a crédito
            </span>
          )}
          {vouchersPendientes > 0 && (
            <span style={{ color: '#ffb74d', fontWeight: 800 }}>
              {vouchersPendientes}
              {' '}
              sin autorización
            </span>
          )}
        </div>
      </header>

      <main style={styles.feed}>
        {error && <div style={styles.error}>{error}</div>}
        {loading && ventas.length === 0 && <MonitorSkeleton />}
        {!loading && ventas.length === 0 && !error && (
          <div style={styles.empty}>Aún no hay ventas registradas hoy.</div>
        )}
        {ventas.map((venta) => (
          <VentaBlock
            key={venta.id_transacion}
            venta={venta}
            highlight={Number(highlightId) === Number(venta.id_transacion)}
            blockRef={(el) => setVentaRef(venta.id_transacion, el)}
          />
        ))}
      </main>

      <footer style={styles.stickyBar}>
        <div style={styles.stickyMediosRow}>
          <StickyCajaItem
            icon={<FaMoneyBillWave />}
            label="Efectivo"
            amount={totales.efectivo}
            iconColor="#81c784"
          />
          <StickyCajaItem
            icon={<FaCreditCard />}
            label="Tarjetas"
            amount={totales.tarjetas}
            iconColor="#64b5f6"
          />
          <StickyCajaItem
            icon={<FaUniversity />}
            label="Transferencia"
            amount={totales.transferencia}
            iconColor="#ffb74d"
          />
          <StickyCajaItem
            icon={<FaExclamationTriangle />}
            label="Crédito"
            amount={totales.credito}
            amountColor={totales.credito > 0 ? '#ff8a80' : undefined}
            iconColor="#ff8a80"
          />
        </div>
        {bancosInline ? (
          <div style={styles.stickyBancosRow}>
            {bancosInline}
          </div>
        ) : null}
      </footer>
    </div>
  );
}

